// Overview — health strip, KPI tiles vs the previous window, daily trends,
// activation funnel, exports by format, assistants (MCP).

import clsx from "clsx";
import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import type { AdminOverview } from "@paceroo/domain/views/admin";
import { Card, Columns, ErrorState, LoadingState, PageHeader } from "../components/ui";
import { useAdminQuery } from "../lib/api";
import { FORMAT_LABEL, fDay, fWDay, n0 } from "../lib/format";
import { bucket, columns, delta, healthCell, isHot, pct, split, sum, type Delta } from "../lib/series";
import { rangeQuery, useRange } from "../lib/useRange";
import { useShell } from "../shell/context";
import s from "./Overview.module.css";

const POLL_MS = 60_000;

interface Kpi {
  label: string;
  value: string;
  unit?: string;
  delta: Delta | null;
  /** Sparkline values for the current window. */
  spark: number[];
  note?: string;
}

export function Overview() {
  const range = useRange();
  const { bp, focusSearch, setUpdated } = useShell();
  const navigate = useNavigate();
  const q = useAdminQuery<AdminOverview>(`overview?days=${range.days}`, POLL_MS);

  useEffect(() => {
    setUpdated(q.updatedAt);
    return () => setUpdated(null);
  }, [q.updatedAt, setUpdated]);

  if (q.error && !q.data) return <ErrorState page="Overview" error={q.error} onRetry={q.reload} onFind={focusSearch} />;
  if (!q.data) return <LoadingState page="overview" />;

  const o = q.data;
  const days = o.range.days;
  const mobile = bp === "mobile";
  const go = (path: string) => navigate(`${path}${rangeQuery(range)}`);
  const kpis = buildKpis(o);
  const tileCols = mobile ? 2 : bp === "tablet" ? 3 : 6;
  const axis = axisLabels(o.days.slice(-days));
  const gap = days >= 90 ? 1 : 2;

  const trend = [
    o.series.activeDaily && {
      label: "Active runners · daily",
      ...split(o.series.activeDaily, days),
      total: (cur: number[]) => `avg ${Math.round(sum(cur) / days)}`,
    },
    {
      label: "Sign-ups · daily",
      ...split(o.series.signups, days),
      total: (cur: number[]) => `${n0(sum(cur))} total`,
    },
  ].filter((t): t is NonNullable<typeof t> => Boolean(t));

  const health = o.health && [
    // `events`: the Events filter that shows exactly what this cell counts.
    { label: "5xx errors", ...healthCell(o.health.errors5xx, 10, 4), events: "group=api" },
    { label: "Auth failures", ...healthCell(o.health.authFailures, 15, 3), events: "q=sign_in_failed" },
    { label: "Rate-limit hits", ...healthCell(o.health.rateLimited, 10, 4), events: "q=rate_limited" },
  ];

  const funnelSteps: Array<[string, number]> = [
    ["Signed up", o.funnel.signedUp],
    ["Verified email", o.funnel.verified],
    ["Started a first plan", o.funnel.startedPlan],
  ];
  if (o.funnel.activeWeek2 !== null) funnelSteps.push(["Active in week 2", o.funnel.activeWeek2]);

  const fmtTotal = o.exportsByFormat ? sum(o.exportsByFormat.map((f) => f.n)) : 0;
  const fmtMax = Math.max(1, ...(o.exportsByFormat ?? []).map((f) => f.n));
  const tools = (o.mcp?.tools ?? []).slice(0, 5);
  const toolMax = Math.max(1, ...tools.map((t) => t.calls));

  return (
    <div className={s.page}>
      <PageHeader kicker={`Overview · through ${fWDay(o.days[o.days.length - 1]!)}`} before="The last " accent={`${days} days.`} />

      {health ? (
        <section className={s.health}>
          {health.map((h) =>
            h.loud ? (
              <div key={h.label} className={s.loud}>
                <span className={s.dot} />
                <div className={s.cellText}>
                  <span className={s.loudLabel}>
                    {h.label} · {n0(h.value)} yesterday
                  </span>
                  <span className="t-mono-xs ink-soft">7-day avg {h.avg}</span>
                  <button className={s.loudLink} onClick={() => navigate(`/events?days=7&${h.events}`)}>
                    See the events →
                  </button>
                </div>
              </div>
            ) : (
              <div key={h.label} className={s.quiet}>
                <span className={s.dot} />
                <div className={s.cellText}>
                  <span className="t-label ink-soft">
                    {h.label} · <span className="t-mono-sm">{n0(h.value)}</span>
                  </span>
                  <span className="t-mono-xs ink-mute">7-day avg {h.avg} · normal</span>
                </div>
              </div>
            ),
          )}
        </section>
      ) : (
        <div className="ob-callout t-body-sm" style={{ color: "var(--ink-soft)" }}>
          Event figures (health, exports, assistants, active runners) need Analytics Engine to be queryable in this environment. Account and plan figures
          below are complete.
        </div>
      )}

      {days >= 90 && o.analytics === "available" && (
        <div className="ob-callout t-body-sm" style={{ color: "var(--ink-soft)" }}>
          Raw events are kept for about 3 months, so the previous 90 days’ event figures (exports, MCP calls, active runners) are incomplete. Sign-ups and
          plans are complete.
        </div>
      )}

      <section className={s.tiles} style={{ gridTemplateColumns: `repeat(${tileCols}, minmax(0, 1fr))` }}>
        {kpis.map((k) => (
          <KpiTile key={k.label} kpi={k} days={days} />
        ))}
      </section>
      <p className={clsx("t-mono-xs ink-mute", s.footnote)}>
        Δ = change vs previous {days} days. ° marks a % on a base under 20 — read the absolute number.
      </p>

      <Card
        head={
          <>
            <span className="kicker ink-mute">Trend · daily</span>
            <div className={s.legend}>
              <span className={clsx("t-mono-xs ink-soft", s.legendItem)}>
                <span className={s.swatch} />
                this period
              </span>
              {range.compare && (
                <span className={clsx("t-mono-xs ink-soft", s.legendItem)}>
                  <span className={s.swatchPrev} />
                  previous period
                </span>
              )}
            </div>
          </>
        }
      >
        <div className={s.trends}>
          {trend.map((t) => (
            <div key={t.label} className={s.trend}>
              <div className={s.trendHead}>
                <span className="t-label">{t.label}</span>
                <span className="t-mono-xs ink-mute">
                  {t.total(t.cur)} · peak {n0(Math.max(0, ...t.cur))}
                </span>
              </div>
              <Columns
                cols={columns(t.cur, range.compare ? t.prev : null)}
                height={mobile ? 84 : 120}
                gap={gap}
                titles={t.cur.map((v, i) => `${fDay(o.days[o.days.length - days + i]!)} · ${n0(v)}`)}
                empty={<span className="t-body-sm ink-mute">Nothing yet. Bars appear as runners arrive.</span>}
              />
            </div>
          ))}
          <div className={s.axis}>
            {axis.map((a) => (
              <span key={a} className="t-mono-xs ink-mute">
                {a}
              </span>
            ))}
          </div>
        </div>
      </Card>

      <div className={s.pair}>
        <Card head={<span className="kicker ink-mute">Activation funnel</span>}>
          <div className={s.stack}>
            {funnelSteps.map(([label, n], i) => {
              const first = funnelSteps[0]![1];
              const before = i > 0 ? funnelSteps[i - 1]![1] : n;
              const isWeek2 = i === 3;
              return (
                <div key={label} className={s.funnelRow}>
                  <div className={s.funnelHead}>
                    <span className={clsx("t-label", s.funnelLabel)}>{label}</span>
                    <span className={clsx("t-mono-md", s.funnelCount)}>{n0(n)}</span>
                    <span className={clsx("t-mono-xs ink-mute", s.funnelShare)}>{pct(n, first)}%</span>
                  </div>
                  <div className={s.track}>
                    <div className={s.fill} style={{ width: `${first ? Math.max(n ? 2 : 0, (n / first) * 100) : 0}%`, background: "var(--ink)" }} />
                  </div>
                  {i > 0 && (
                    <span className="t-mono-xs ink-mute">
                      {isWeek2
                        ? `${n0(n)} of ${n0(o.funnel.eligibleWeek2)} eligible came back`
                        : `−${n0(before - n)} (${100 - pct(n, before)}%)`}
                    </span>
                  )}
                </div>
              );
            })}
            <p className={clsx("t-caption ink-mute", s.note)}>
              Cohort: runners who signed up in the last {days} days.
              {o.funnel.activeWeek2 === null
                ? " Week-2 activity needs Analytics Engine."
                : " Week-2 activity only counts runners who signed up 14+ days ago and started a plan."}
            </p>
          </div>
        </Card>

        <Card
          head={
            <>
              <span className="kicker ink-mute">Exports by format · {o.exportsByFormat ? n0(fmtTotal) : "—"}</span>
              <button className={clsx("btn-tertiary", s.linkBtn)} onClick={() => go("/exports")}>
                Exports →
              </button>
            </>
          }
        >
          <div className={s.stack}>
            {!o.exportsByFormat && <span className="t-body-sm ink-mute">Needs Analytics Engine.</span>}
            {o.exportsByFormat?.length === 0 && <span className="t-body-sm ink-mute">No downloads in this period.</span>}
            {o.exportsByFormat?.map((f) => (
              <div key={f.format} className={s.fmtRow}>
                <span className={clsx("t-label", s.ellipsis)}>{FORMAT_LABEL[f.format] ?? f.format}</span>
                <div className={s.track}>
                  <div className={s.fill} style={{ width: `${(f.n / fmtMax) * 100}%`, background: "var(--ink-soft)" }} />
                </div>
                <span className={clsx("t-mono-sm", s.right)}>
                  {n0(f.n)} <span className="ink-mute">{pct(f.n, fmtTotal)}%</span>
                </span>
              </div>
            ))}
          </div>
        </Card>
      </div>

      <Card
        head={
          <>
            <span className="kicker ink-mute">AI assistants · MCP</span>
            <button className={clsx("btn-tertiary", s.linkBtn)} onClick={() => go("/assistants")}>
              All assistants →
            </button>
          </>
        }
      >
        {!o.mcp ? (
          <span className="t-body-sm ink-mute">Needs Analytics Engine.</span>
        ) : (
          <div className={s.mcpGrid}>
            <div className={s.mcpStats}>
              <Stat label="Active MCP runners" value={n0(o.mcp.runners)} />
              <Stat label="Calls" value={n0(o.mcp.calls)} />
              <Stat label="Error rate" value={`${o.mcp.calls ? ((o.mcp.errors / o.mcp.calls) * 100).toFixed(1) : "0.0"}%`} />
              <Stat label="Rate-limited" value={n0(o.mcp.rateLimited)} />
            </div>
            <div className={s.tools}>
              <div className={s.toolRow}>
                <span className="t-mono-xs ink-mute">TOP TOOLS</span>
                <span />
                <span className={clsx("t-mono-xs ink-mute", s.right)}>CALLS</span>
                <span className={clsx("t-mono-xs ink-mute", s.right)}>ERR</span>
              </div>
              {tools.length === 0 && <span className="t-body-sm ink-mute">No calls in this period.</span>}
              {tools.map((t) => {
                const hot = isHot(t.calls, t.errors);
                return (
                  <div key={t.tool} className={s.toolRow}>
                    <span className={clsx("t-mono-sm", s.ellipsis)}>{t.tool}</span>
                    <div className={s.trackSm}>
                      <div className={s.toolFill} style={{ width: `${(t.calls / toolMax) * 100}%` }}>
                        <div className={s.toolErr} style={{ width: `${t.calls ? (t.errors / t.calls) * 100 : 0}%`, minWidth: t.errors ? 2 : 0 }} />
                      </div>
                    </div>
                    <span className={clsx("t-mono-sm", s.right)}>{n0(t.calls)}</span>
                    <span className={clsx("t-mono-sm", s.right)} style={{ color: hot ? "var(--alert)" : "var(--ink-soft)" }}>
                      {t.calls ? ((t.errors / t.calls) * 100).toFixed(1) : "0.0"}%
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </Card>
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className={s.mcpStat}>
      <span className="kicker ink-mute">{label}</span>
      <span className="pa-stat-value">{value}</span>
    </div>
  );
}

function KpiTile({ kpi, days }: { kpi: Kpi; days: number }) {
  const d = kpi.delta;
  const toneColor = d?.tone === "down" ? "var(--alert)" : d?.tone === "up" ? "var(--success)" : "var(--ink-mute)";
  const muted = d ? d.small || d.rel === "new" : true;
  const spark = bucket(kpi.spark, days);
  const cols = columns(spark);
  return (
    <div className={s.tile}>
      <span className="kicker ink-mute">{kpi.label}</span>
      <div className={s.valueRow}>
        <span className={clsx("pa-stat-value", s.value)}>{kpi.value}</span>
        {kpi.unit && <span className="t-mono-sm ink-mute">{kpi.unit}</span>}
      </div>
      <div className={s.deltaRow}>
        {d ? (
          <>
            <span className={clsx("t-mono-sm", s.deltaAbs)} style={{ color: toneColor }}>
              {d.abs}
            </span>
            {d.rel && (
              <span
                className="t-mono-sm"
                style={{ color: muted ? "var(--ink-mute)" : toneColor }}
                title={d.small ? `Small base (prev ${d.prev}): treat the % with care` : undefined}
              >
                {d.rel}
                {d.small ? "°" : ""}
              </span>
            )}
            <span className="t-mono-xs ink-mute">
              prev {n0(d.prev)}
              {kpi.unit ?? ""}
            </span>
          </>
        ) : (
          <span className="t-mono-xs ink-mute">{kpi.note ?? "—"}</span>
        )}
      </div>
      <div className={s.spark}>
        {cols.map((c, i) => (
          <div key={i} className={s.sparkCol} style={{ height: `${c.h}%`, background: i === cols.length - 1 ? "var(--ink)" : "var(--ink-mute)" }} />
        ))}
      </div>
    </div>
  );
}

function buildKpis(o: AdminOverview): Kpi[] {
  const days = o.range.days;
  const total = (label: string, series: number[] | null): Kpi => {
    if (!series) return { label, value: "—", delta: null, spark: [], note: "needs Analytics Engine" };
    const { prev, cur } = split(series, days);
    return { label, value: n0(sum(cur)), delta: delta(sum(cur), sum(prev)), spark: cur };
  };
  const signups = split(o.series.signups, days);
  const verified = split(o.series.verified, days);
  const vCur = pct(sum(verified.cur), sum(signups.cur));
  const vPrev = pct(sum(verified.prev), sum(signups.prev));
  const weekly = o.series.activeWeekly ? split(o.series.activeWeekly, days) : null;
  return [
    total("Sign-ups", o.series.signups),
    {
      label: "Verified",
      value: String(vCur),
      unit: "%",
      delta: delta(vCur, vPrev, " pt"),
      spark: signups.cur.map((n, i) => (n ? Math.round(((verified.cur[i] ?? 0) / n) * 100) : 0)),
    },
    weekly
      ? {
          label: "Weekly active runners",
          value: n0(weekly.cur[weekly.cur.length - 1] ?? 0),
          delta: delta(weekly.cur[weekly.cur.length - 1] ?? 0, weekly.prev[weekly.prev.length - 1] ?? 0),
          spark: weekly.cur,
        }
      : { label: "Weekly active runners", value: "—", delta: null, spark: [], note: "needs Analytics Engine" },
    total("Plans started", o.series.plansStarted),
    total("Exports", o.series.exports),
    total("MCP calls", o.series.mcpCalls),
  ];
}

/** Five evenly spaced day labels under the trend charts. */
function axisLabels(days: string[]): string[] {
  if (days.length === 0) return [];
  const out: string[] = [];
  for (let k = 0; k <= 4; k++) out.push(fDay(days[Math.round((k * (days.length - 1)) / 4)]!));
  return [...new Set(out)];
}
